import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SmartSegmentationHeader } from '../components/SmartSegmentationHeader';
import { Footer } from '../components/Footer';
import { ChevronDownIcon, CheckCircleIcon, XIcon, LoaderIcon } from 'lucide-react';
import { SegmentDashboardMockup } from '../components/SegmentDashboardMockup';
import { SponsorReportMockup } from '../components/SponsorReportMockup';
import { FlowDiagramMockup } from '../components/FlowDiagramMockup';
import { OverwhelmedCreatorMockup } from '../components/OverwhelmedCreatorMockup';
import { getBaseUrl } from '../utils/api';
export function SmartSegmentationPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [cognitoId, setCognitoId] = useState<string | null>(null);
  const [userActivities, setUserActivities] = useState<string[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [hasSignedUpViaEmail, setHasSignedUpViaEmail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailDialogRef = useRef<HTMLDivElement>(null);

  // Check localStorage for email signup on mount
  useEffect(() => {
    const emailSignupStatus = localStorage.getItem('ss_beta_join_email');
    if (emailSignupStatus === 'true') {
      setHasSignedUpViaEmail(true);
    }
  }, []);

  // Function to fetch user data
  const fetchUserData = useCallback(async (id: string) => {
    setIsLoadingUser(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user?cognitoId=${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.user) {
          setUserName(data.user.name);
          setUserActivities(data.user.activity || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  // Fetch user data on page load if id parameter exists
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setCognitoId(id);
      fetchUserData(id);
    }
  }, [fetchUserData]);

  // Handle email dialog outside clicks and ESC key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emailDialogRef.current && !emailDialogRef.current.contains(event.target as Node)) {
        setShowEmailDialog(false);
      }
    };
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowEmailDialog(false);
      }
    };
    if (showEmailDialog) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'auto';
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [showEmailDialog]);

  // Reset email input when dialog closes
  useEffect(() => {
    if (!showEmailDialog) {
      setEmailInput('');
    }
  }, [showEmailDialog]);
  const handleJoinBetaClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    // If user is known (has cognitoId and userName), POST activity directly
    if (cognitoId && userName) {
      setIsSubmitting(true);
      try {
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}user/activity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            activity_name: 'ss_beta_join',
            cognito_id: cognitoId
          })
        });
        if (response.ok) {
          console.log('Activity logged successfully');
          // Re-fetch user data to update activities
          if (cognitoId) {
            await fetchUserData(cognitoId);
          }
        } else {
          console.error('Failed to log activity');
        }
      } catch (error) {
        console.error('Error logging activity:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // User not known - show email dialog
      setShowEmailDialog(true);
    }
  };
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput) return;
    setIsSubmitting(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activity_name: 'ss_beta_join',
          cognito_id: '1',
          activity_data: {
            email: emailInput
          }
        })
      });
      if (response.ok) {
        // Save signup status to localStorage
        localStorage.setItem('ss_beta_join_email', 'true');
        setHasSignedUpViaEmail(true);
        setShowEmailDialog(false);
        setEmailInput('');
        console.log('Email submitted successfully');
      } else {
        console.error('Failed to submit email');
      }
    } catch (error) {
      console.error('Error submitting email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Extract first name from full name
  const firstName = userName ? userName.split(' ')[0] : null;

  // Check if user has already signed up for beta (via API or email)
  const isSignedUpForBeta = userActivities.includes('ss_beta_join') || hasSignedUpViaEmail;
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    question: 'How does this connect to Kit.com?',
    answer: 'You securely connect your Kit.com account, and we read your opens, clicks, and product data to build segments. No extra tools required.'
  }, {
    question: 'Is my data safe?',
    answer: "Yes. Your data is encrypted, only used to power your own segments and insights, and you can disconnect at any time. We don't sell your data."
  }, {
    question: 'Who is this for?',
    answer: 'Newsletter creators who send via Kit.com, have a growing list, and either run sponsorships now or plan to soon.'
  }, {
    question: 'What does "beta" mean?',
    answer: 'You get early access, closer support, and a say in the roadmap. The product will improve quickly based on feedback.'
  }, {
    question: 'How much does it cost?',
    answer: 'Pricing is still being finalized. Beta users get early adopter pricing and will see plans before committing to anything.'
  }, {
    question: 'How much setup is required?',
    answer: "Connect Kit.com, let us process your data, review your segments, and you're ready to use them in campaigns and pitches."
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1956">
    <SmartSegmentationHeader data-id="element-1957" />

    {isLoadingUser ? <main className="flex-grow pt-24 flex items-center justify-center min-h-[60vh]" data-id="element-1958">
      <div className="text-center" data-id="element-1959">
        <LoaderIcon className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" data-id="element-1960" />
        <p className="text-gray-600 text-lg" data-id="element-1961">Loading...</p>
      </div>
    </main> : <main className="flex-grow pt-24" data-id="element-1962">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1963">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1964">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-6" data-id="element-1965">
            <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" data-id="element-1966"></span>
            <span className="text-sm font-medium text-blue-700" data-id="element-1967">
              Always-on segmentation for Kit.com creators
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1968">
            {firstName ? `Still manually tagging subscribers, ${firstName}? This was built for you` : 'Still manually tagging subscribers? This was built for you'}
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto" data-id="element-1969">
            AI that turns your data into automated smart segments, so your
            audience stays organized, personalized, and sponsor-ready without
            you touching a single tag.
          </p>

          <ul className="space-y-3 mb-8 inline-block text-left" data-id="element-1970">
            <li className="flex items-start text-lg" data-id="element-1971">
              <span className="mr-3" data-id="element-1972">🎯</span>
              <span data-id="element-1973">Send targeted content to the right people</span>
            </li>
            <li className="flex items-start text-lg" data-id="element-1974">
              <span className="mr-3" data-id="element-1975">⏱</span>
              <span data-id="element-1976">Save hours by killing manual tagging</span>
            </li>
            <li className="flex items-start text-lg" data-id="element-1977">
              <span className="mr-3" data-id="element-1978">💸</span>
              <span data-id="element-1979">Pitch higher-value, segment-based sponsorships</span>
            </li>
          </ul>

          <div data-id="element-1980">
            {isSignedUpForBeta ? <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 max-w-md mx-auto" data-id="element-1981">
              <p className="text-xl font-semibold text-gray-900 text-center mb-2" data-id="element-1982">
                🎉 You're signed up for the beta! 🎉
              </p>
              <p className="text-gray-700 text-center" data-id="element-1983">
                You will hear back from us soon.
              </p>
            </div> : <>
              {firstName && <p className="text-lg text-gray-700 mb-4" data-id="element-1984">
                {firstName}, ready to join the beta?
              </p>}
              <button onClick={handleJoinBetaClick} disabled={isSubmitting} className="inline-block px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1985">
                Join the Beta
              </button>
              <p className="text-sm text-gray-500 mt-3" data-id="element-1986">
                Early access • Limited spots for Kit.com creators
              </p>
            </>}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-1987">
        <div className="max-w-6xl mx-auto" data-id="element-1988">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1989">
            <div className="order-2 lg:order-1" data-id="element-1990">
              <OverwhelmedCreatorMockup data-id="element-1991" />
            </div>

            <div className="order-1 lg:order-2" data-id="element-1992">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1993">
                The problem: you're doing ops instead of growing your
                newsletter
              </h2>
              <p className="text-lg text-gray-700 mb-6" data-id="element-1994">
                Right now, you probably:
              </p>
              <ul className="space-y-3 mb-6" data-id="element-1995">
                <li className="flex items-start text-lg text-gray-700" data-id="element-1996">
                  <span className="mr-3 text-gray-400" data-id="element-1997">•</span>
                  <span data-id="element-1998">
                    Manually tag subscribers or avoid tagging altogether
                  </span>
                </li>
                <li className="flex items-start text-lg text-gray-700" data-id="element-1999">
                  <span className="mr-3 text-gray-400" data-id="element-2000">•</span>
                  <span data-id="element-2001">
                    Send the same email to totally different types of readers
                  </span>
                </li>
                <li className="flex items-start text-lg text-gray-700" data-id="element-2002">
                  <span className="mr-3 text-gray-400" data-id="element-2003">•</span>
                  <span data-id="element-2004">
                    Share generic, one-size-fits-all stats with sponsors
                  </span>
                </li>
              </ul>
              <p className="text-xl font-semibold text-gray-900" data-id="element-2005">
                Result:{' '}
                <span className="text-blue-600" data-id="element-2006">
                  time wasted, generic content, and money left on the table.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-2007">
        <div className="max-w-6xl mx-auto" data-id="element-2008">
          <div className="text-center mb-16" data-id="element-2009">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2010">
              Let your Kit.com list segment itself
            </h2>
          </div>

          {/* How it works */}
          <div className="mb-16" data-id="element-2011">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-2012">
              How it works
            </h3>

            <div className="flex items-center justify-center mb-12" data-id="element-2013">
              <div className="w-full max-w-5xl" data-id="element-2014">
                <FlowDiagramMockup data-id="element-2015" />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2016">
            <div className="flex items-start" data-id="element-2017">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-2018" />
              <div data-id="element-2019">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2020">
                  Hands-free segmentation
                </h4>
                <p className="text-gray-600" data-id="element-2021">
                  No rules to maintain, no manual tagging.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2022">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-2023" />
              <div data-id="element-2024">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2025">
                  Clear "who's who"
                </h4>
                <p className="text-gray-600" data-id="element-2026">
                  Instantly see segments like "Deal hunters," "Founders," or
                  "Sponsor clickers."
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2027">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-2028" />
              <div data-id="element-2029">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2030">
                  Make your newsletter feel 1:1
                </h4>
                <p className="text-gray-600" data-id="element-2031">
                  Effortlessly send different messages and offers to each
                  segment.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-2032">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-2033" />
              <div data-id="element-2034">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2035">
                  Sponsor-ready segment stats
                </h4>
                <p className="text-gray-600" data-id="element-2036">
                  Use segment data directly in media kits and pitches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-2037">
        <div className="max-w-6xl mx-auto" data-id="element-2038">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-2039">
            What you get with Smart Audience Segmentation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-2040">
            <div className="bg-white rounded-xl p-8" data-id="element-2041">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2042">
                Save time & mental energy
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2043">
                <li className="flex items-start" data-id="element-2044">
                  <span className="mr-2" data-id="element-2045">•</span>
                  <span data-id="element-2046">No building segments by hand</span>
                </li>
                <li className="flex items-start" data-id="element-2047">
                  <span className="mr-2" data-id="element-2048">•</span>
                  <span data-id="element-2049">No hunting through CSVs and past campaigns</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8" data-id="element-2050">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2051">
                Send more relevant content
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2052">
                <li className="flex items-start" data-id="element-2053">
                  <span className="mr-2" data-id="element-2054">•</span>
                  <span data-id="element-2055">Create segment-specific content and offers</span>
                </li>
                <li className="flex items-start" data-id="element-2056">
                  <span className="mr-2" data-id="element-2057">•</span>
                  <span data-id="element-2058">
                    Reduce unsubscribes from "this isn't for me" emails
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8" data-id="element-2059">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2060">
                Grow faster
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2061">
                <li className="flex items-start" data-id="element-2062">
                  <span className="mr-2" data-id="element-2063">•</span>
                  <span data-id="element-2064">
                    Double down on the segments that open, click, and buy the
                    most
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2065">
                  <span className="mr-2" data-id="element-2066">•</span>
                  <span data-id="element-2067">Spot winning topics and products fast</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8" data-id="element-2068">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2069">
                Pitch sponsors with confidence
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2070">
                <li className="flex items-start" data-id="element-2071">
                  <span className="mr-2" data-id="element-2072">•</span>
                  <span data-id="element-2073">
                    Show segment-level stats instead of just list size
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2074">
                  <span className="mr-2" data-id="element-2075">•</span>
                  <span data-id="element-2076">
                    Offer targeted placements to the exact audience they care
                    about
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-center" data-id="element-2077">
            <div className="w-full max-w-2xl" data-id="element-2078">
              <SegmentDashboardMockup data-id="element-2079" />
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-2080">
        <div className="max-w-6xl mx-auto" data-id="element-2081">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2082">
            <div data-id="element-2083">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-2084">
                Turn segments into premium ad inventory
              </h2>
              <p className="text-lg text-gray-700 mb-6" data-id="element-2085">
                Use Smart Audience Segmentation to:
              </p>
              <ul className="space-y-4" data-id="element-2086">
                <li className="flex items-start" data-id="element-2087">
                  <span className="mr-3 text-blue-600 font-bold" data-id="element-2088">•</span>
                  <div data-id="element-2089">
                    <span className="font-semibold text-gray-900" data-id="element-2090">
                      Justify higher rates
                    </span>
                    <span className="text-gray-700" data-id="element-2091">
                      {' '}
                      by showing "This segment of 4,000+ solo travelers has X%
                      open rate and Y% CTR."
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2092">
                  <span className="mr-3 text-blue-600 font-bold" data-id="element-2093">•</span>
                  <div data-id="element-2094">
                    <span className="font-semibold text-gray-900" data-id="element-2095">
                      Create sponsor-specific pitches
                    </span>
                    <span className="text-gray-700" data-id="element-2096">
                      {' '}
                      like "Reach our 'Active Buyers' segment—subscribers who
                      frequently click sponsor links."
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-2097">
                  <span className="mr-3 text-blue-600 font-bold" data-id="element-2098">•</span>
                  <div data-id="element-2099">
                    <span className="font-semibold text-gray-900" data-id="element-2100">
                      Report performance by segment
                    </span>
                    <span className="text-gray-700" data-id="element-2101">
                      {' '}
                      so sponsors see exactly who engaged and why they should
                      rebook.
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-center" data-id="element-2102">
              <div className="w-full max-w-lg" data-id="element-2103">
                <SponsorReportMockup data-id="element-2104" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-2105">
        <div className="max-w-6xl mx-auto" data-id="element-2106">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center" data-id="element-2107">
            Built for serious Kit.com newsletter creators
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center max-w-3xl mx-auto" data-id="element-2108">
            Perfect if you:
          </p>
          <ul className="space-y-3 max-w-2xl mx-auto" data-id="element-2109">
            <li className="flex items-start text-lg" data-id="element-2110">
              <span className="mr-3" data-id="element-2111">✓</span>
              <span data-id="element-2112">Send at least 1–2 newsletters per week on Kit.com</span>
            </li>
            <li className="flex items-start text-lg" data-id="element-2113">
              <span className="mr-3" data-id="element-2114">✓</span>
              <span data-id="element-2115">
                Run sponsorships, affiliates, or your own product offers
              </span>
            </li>
            <li className="flex items-start text-lg" data-id="element-2116">
              <span className="mr-3" data-id="element-2117">✓</span>
              <span data-id="element-2118">
                Want smarter targeting without becoming a data analyst
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-2119">
        <div className="max-w-3xl mx-auto" data-id="element-2120">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-2121">
            FAQ
          </h2>
          <div className="space-y-4" data-id="element-2122">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg" data-id="element-2123">
              <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-2124">
                <span className="font-semibold text-gray-900" data-id="element-2125">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform ${openFaqIndex === index ? 'transform rotate-180' : ''}`} data-id="element-2126" />
              </button>
              {openFaqIndex === index && <div className="px-6 pb-4" data-id="element-2127">
                <p className="text-gray-700" data-id="element-2128">{faq.answer}</p>
              </div>}
            </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="join-beta" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-2129">
        <div className="max-w-4xl mx-auto text-center" data-id="element-2130">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-2131">
            Ready to stop guessing who's on your list?
          </h2>
          <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto" data-id="element-2132">
            Turn your Kit.com data into auto-updating audience segments that
            power better content and better sponsorship deals.
          </p>
          <div data-id="element-2133">
            {isSignedUpForBeta ? <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 max-w-md mx-auto" data-id="element-2134">
              <p className="text-xl font-semibold text-gray-900 text-center mb-2" data-id="element-2135">
                🎉 You're signed up for the beta! 🎉
              </p>
              <p className="text-gray-700 text-center" data-id="element-2136">
                You will hear back from us soon.
              </p>
            </div> : <>
              {firstName && <p className="text-lg text-gray-700 mb-4" data-id="element-2137">
                {firstName}, ready to join the beta?
              </p>}
              <button onClick={handleJoinBetaClick} disabled={isSubmitting} className="inline-block px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2138">
                Join the Beta
              </button>
              <p className="text-sm text-gray-600 mt-4" data-id="element-2139">
                We're inviting a small group of Kit.com creators first so we can
                support you closely.
              </p>
            </>}
          </div>
        </div>
      </section>
    </main>}

    {/* Email Dialog */}
    {showEmailDialog && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-2140">
      <div ref={emailDialogRef} className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden" data-id="element-2141">
        <div className="flex justify-end p-4" data-id="element-2142">
          <button onClick={() => setShowEmailDialog(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2143">
            <XIcon className="w-6 h-6" data-id="element-2144" />
          </button>
        </div>
        <div className="px-6 pb-6 pt-2" data-id="element-2145">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-2146">
            Join the Beta
          </h2>
          <p className="text-gray-600 text-center mb-6" data-id="element-2147">
            Enter your email to join the beta program
          </p>
          <form className="space-y-4" onSubmit={handleEmailSubmit} data-id="element-2148">
            <div data-id="element-2149">
              <input type="email" placeholder="Your email address" required value={emailInput} onChange={e => setEmailInput(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" data-id="element-2150" />
            </div>
            <button type="submit" className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg transition-all hover:shadow-md hover:bg-blue-700 flex items-center justify-center" disabled={isSubmitting} data-id="element-2151">
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </div>}

    <Footer data-id="element-2152" />
  </div>;
}